'use client';

import { X } from 'lucide-react';
import Image from 'next/image';
import type { MouseEvent } from 'react';
import { useLayoutEffect } from 'react';
import {
	buildMobileAppBannerLink,
	detectMobileAppBannerPlatform,
	MOBILE_APP_BANNER_ATTRIBUTE,
	MOBILE_APP_BANNER_BOOT_SCRIPT,
	MOBILE_APP_BANNER_DISMISS_KEY,
	MOBILE_APP_BANNER_THEME_COLOR,
	type MobileAppBannerPlatform,
	nativeRouteFromPathname,
} from '@/lib/mobile-app-banner';

const HEADLINE = 'Download the App';
const TAGLINE = 'New drops. Restock alerts.';
const PLATFORMS: readonly MobileAppBannerPlatform[] = ['ios', 'android'];

/**
 * Runs while the HTML parses, before anything below it paints. Client renders
 * get an inert copy: React never executes scripts it inserts, and the type
 * swap keeps React's development warning about rendered <script> tags quiet
 * (see the Next.js "preventing flash before hydration" guide).
 */
function InlineScript({ html }: { html: string }) {
	return (
		<script
			type={typeof window === 'undefined' ? 'text/javascript' : 'text/plain'}
			suppressHydrationWarning
			// biome-ignore lint/security/noDangerouslySetInnerHtml: static, dependency-free boot script that must run before the bundle loads
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
}

function showBanner(platform: MobileAppBannerPlatform) {
	document.documentElement.setAttribute(MOBILE_APP_BANNER_ATTRIBUTE, platform);
	// Browsers pick the chrome tint from `theme-color` when present and from
	// the page background otherwise. Only override while the banner is up so
	// dismissing it hands control straight back to the page.
	if (document.querySelector(`meta[name="theme-color"][${MOBILE_APP_BANNER_ATTRIBUTE}]`)) return;
	const meta = document.createElement('meta');
	meta.name = 'theme-color';
	meta.content = MOBILE_APP_BANNER_THEME_COLOR;
	meta.setAttribute(MOBILE_APP_BANNER_ATTRIBUTE, '');
	document.head.prepend(meta);
}

function hideBanner() {
	document.documentElement.removeAttribute(MOBILE_APP_BANNER_ATTRIBUTE);
	for (const meta of document.querySelectorAll(`meta[${MOBILE_APP_BANNER_ATTRIBUTE}]`)) {
		meta.remove();
	}
}

/**
 * The banner is part of every page's HTML: the storefront shell is cached per
 * market, never per device, so the server cannot leave it out for desktops.
 * Whether it shows is decided by the inline boot script rendered just ahead of
 * it, which tags <html> with the platform while the HTML parses; the rules in
 * globals.css then reveal the banner and the matching store button. That puts
 * the banner in the first paint. Mounting it after hydration, as this
 * component used to, pushed everything already on screen down by its height.
 * iOS Safari never gets this banner: Safari draws Apple's own at the top of
 * the page (see `isIosSafari`), and the two would stack.
 */
export function MobileAppBanner() {
	useLayoutEffect(() => {
		// The boot script has normally run by now. React's development Strict
		// Mode remount resets <html> to the attributes it manages, and a
		// client-side mount never parses the script, so repeat its decision here
		// whenever the attribute is missing.
		if (document.documentElement.hasAttribute(MOBILE_APP_BANNER_ATTRIBUTE)) return;
		const platform = detectMobileAppBannerPlatform({
			userAgent: navigator.userAgent,
			platform: navigator.platform,
			maxTouchPoints: navigator.maxTouchPoints,
		});
		if (!platform) return;
		if (window.matchMedia('(display-mode: standalone)').matches) return;
		try {
			if (sessionStorage.getItem(MOBILE_APP_BANNER_DISMISS_KEY) === '1') return;
		} catch {
			// The banner still works when browser storage is unavailable.
		}
		showBanner(platform);
	}, []);

	function dismiss() {
		hideBanner();
		try {
			sessionStorage.setItem(MOBILE_APP_BANNER_DISMISS_KEY, '1');
		} catch {
			// Dismiss for this page view even when storage is unavailable.
		}
	}

	// The route is read at tap time from the live URL rather than a router hook:
	// `usePathname` would block prerendering of the shared storefront shell.
	function openApp(event: MouseEvent<HTMLAnchorElement>, platform: MobileAppBannerPlatform) {
		const current = buildMobileAppBannerLink(
			platform,
			nativeRouteFromPathname(window.location.pathname),
		);
		// Keep a real, user-initiated link navigation so iOS can hand the HTTPS
		// link to the app. Read the current page at click time after SPA navigation.
		event.currentTarget.href = current.href;
	}

	return (
		<>
			<InlineScript html={MOBILE_APP_BANNER_BOOT_SCRIPT} />
			<aside
				aria-label="Workman mobile app"
				className="mobile-app-banner border-b border-neutral-200 bg-white text-neutral-950"
			>
				<div className="container-page">
					{/* Layout: app icon, title over tagline, store pill, then the close
					    control on the far edge so the eye lands on the copy and CTA first. */}
					<div className="flex items-center gap-3 py-3">
						<Image
							src="/app-icon.png"
							alt=""
							width={40}
							height={40}
							className="size-10 shrink-0 rounded-lg"
						/>
						<div className="min-w-0 flex-1">
							<p className="truncate text-[15px] leading-[19px] font-semibold tracking-tight">
								{HEADLINE}
							</p>
							<p className="mt-px truncate text-[13px] leading-[17px] text-neutral-500">
								{TAGLINE}
							</p>
						</div>
						{/* One button per store; globals.css shows the one for the tagged platform. */}
						{PLATFORMS.map((platform) => {
							const link = buildMobileAppBannerLink(platform, null);
							return (
								<a
									key={platform}
									data-platform={platform}
									href={link.href}
									aria-label={link.label}
									onClick={(event) => openApp(event, platform)}
									className="group min-h-11 shrink-0 items-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-950"
								>
									<span className="flex h-8 items-center rounded-full bg-neutral-950 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-white transition-colors group-hover:bg-neutral-800">
										{link.cta}
									</span>
								</a>
							);
						})}
						<button
							type="button"
							onClick={dismiss}
							aria-label="Dismiss app banner"
							className="-mr-2.5 flex h-11 w-10 shrink-0 items-center justify-center text-neutral-950 transition-colors hover:text-neutral-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-950"
						>
							<X className="size-5" strokeWidth={1.5} aria-hidden="true" />
						</button>
					</div>
				</div>
			</aside>
		</>
	);
}
